import * as React from 'react';
import { AmexIcon } from '@tock/shared/components/icons/CreditCardIcons';
import { DisplayDate, DisplaySchedule, DisplayTime } from '@tock/time';
import { IconClock, IconLogoAmericanExpress, IconTicket } from '@tock/icons';
import { IconClock as IconClockLegacy, IconEvents, IconPeople } from 'ui/icons';
import { IconMigrationUtil, useIsSakeIconSetEnabled } from 'omakase/utils/IconMigrationUtil';
import { LocalDate, LocalDateTime, ZoneOffset } from 'js-joda';
import { OfferingDetailsPrice } from 'components/business/offering/OfferingDetailsPrice';
import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';
import { getSizeContent, getTypeContent } from 'utils/experience';
import {
  isOfferedOnOneDateAndAtOneTime,
  isOfferingEventType,
  isOfferingGAEventType,
  isOfferingSeatedEventType,
} from 'utils/offerings';
import Location from 'components/business/location/Location';
import OfferingDetailsAdditionalCosts from 'components/business/offering/OfferingDetailsAdditionalCosts';
import OfferingDetailsMetadataItem from 'components/business/offering/OfferingDetailsMetadataItem';
import _ from 'lodash';

/**
 * Warning!
 * The logic for showing items needs to match the logic for counting items in
 * utils/offerings.ts
 */

interface Props {
  business: consumer.IBusinessConfig;
  hideLocation?: boolean;
  offering: consumer.Offering;
  onNavigateToExperience?(event: consumer.Offering, nextAvailableDate?: string): void;
}

const EventOfferingDetailsSimple: React.FunctionComponent<Props> = ({
  business,
  hideLocation = false,
  offering,
  onNavigateToExperience,
}) => {
  const isSakeIconSetEnabled = useIsSakeIconSetEnabled();

  const {
    descriptiveVariety,
    eventDetails,
    isAmexOnly,
    isAtBusinessLocation,
    isOnOneDate,
    name,
    singleUnitQuantity,
    purchaseSize,
    type,
  } = offering;
  // Used in Search Modal when clicking on an experience name
  const handleNavigateToExperience = () => {
    if (onNavigateToExperience) {
      onNavigateToExperience(offering);
    }
  };

  const getSingleDateTimeDisplay = (
    offering: consumer.IOffering,
    eventDetails: consumer.EventDetails
  ): React.ReactNode => {
    const { isAtOneTime } = offering;
    const { startTime, endTime, eventFirstDatetimeSecs } = eventDetails;
    const eventFirstDatetime = eventFirstDatetimeSecs
      ? LocalDateTime.ofEpochSecond(eventFirstDatetimeSecs, ZoneOffset.UTC)
      : null;
    if (startTime) {
      return (
        <>
          {!isAtOneTime && <span>Multiple times, starting at </span>}
          <DisplayTime timeValue={startTime} />
          {!_.isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }

    if (eventFirstDatetime) {
      return (
        <>
          <DisplayTime timeValue={eventFirstDatetime} />
        </>
      );
    }

    return null;
  };

  const getMultiDateTemplateTimeDisplay = (
    offering: consumer.IOffering,
    eventDetails: consumer.EventDetails
  ): React.ReactNode => {
    const { isAtOneTime } = offering;
    const { startTime, endTime } = eventDetails;
    if (isAtOneTime) {
      return (
        <>
          <DisplayTime timeValue={startTime} />
          {!_.isNil(endTime) && (
            <span>
              {' '}
              - <DisplayTime timeValue={endTime} />
            </span>
          )}
        </>
      );
    }
    return <>Multiple times</>;
  };

  if (!isOfferingEventType(offering) || _.isNil(eventDetails)) {
    return null;
  }

  const { date, location } = eventDetails;
  const schedule = eventDetails?.schedule?.[0];

  const timeRange = isOnOneDate
    ? getSingleDateTimeDisplay(offering, eventDetails as consumer.EventDetails)
    : getMultiDateTemplateTimeDisplay(offering, eventDetails as consumer.EventDetails);

  // The second line of this can be removed when the old, non-specific EVENT variety is fully removed
  const showEventSize =
    isOfferingSeatedEventType(offering) ||
    (!isOfferingGAEventType(offering) && !isOfferedOnOneDateAndAtOneTime(offering));

  const dataTestIdValue = offering.name.replace(/\W/g, '');

  const price = offering.price as consumer.OfferedPriceSummary;

  const navigate = !!onNavigateToExperience;

  const hasSchedule = !_.isNil(schedule);

  const experienceTitle = (
    <Typography variant="body01Strong" component="h2" mb={1} noWrap>
      {name}
    </Typography>
  );

  return (
    <div data-testid={dataTestIdValue}>
      <div className="Consumer-reservationInfo">
        {navigate && (
          <button
            aria-label={`View ${name} (opens a dialog)`}
            onClick={handleNavigateToExperience}
            className="Consumer-reservationLink"
          >
            {experienceTitle}
          </button>
        )}
        {!navigate && <>{experienceTitle}</>}
      </div>

      <ul>
        {isAmexOnly && (
          <span className="Consumer-reservationMetaItem">
            <div className="Consumer-reservationMetaItemIcon">
              <IconMigrationUtil
                legacyIcon={
                  <AmexIcon contextClassName="Consumer-reservationMetaItemIcon-amexExclusive" />
                }
                newIcon={<IconLogoAmericanExpress />}
              />
            </div>
            <Typography component="h2" variant="body02">
              American Express® Card exclusive
            </Typography>
          </span>
        )}
        {showEventSize && (
          <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconTicket : IconPeople}>
            {getTypeContent(type, purchaseSize, descriptiveVariety, singleUnitQuantity)}
            {getSizeContent(type, purchaseSize)}
          </OfferingDetailsMetadataItem>
        )}

        {hasSchedule && (
          <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconTicket : IconEvents}>
            <DisplaySchedule
              schedule={schedule as core.Schedule}
              filterDatesEarlierThan={LocalDate.now()}
            />
          </OfferingDetailsMetadataItem>
        )}
        {!hasSchedule && date && (
          <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconTicket : IconEvents}>
            <DisplayDate dateValue={date} displayMonthAs="short" showWeekday />
          </OfferingDetailsMetadataItem>
        )}

        {timeRange && (
          <OfferingDetailsMetadataItem icon={isSakeIconSetEnabled ? IconClock : IconClockLegacy}>
            {timeRange}
          </OfferingDetailsMetadataItem>
        )}
        <OfferingDetailsPrice
          price={price}
          type={type}
          descriptiveVariety={descriptiveVariety}
          singleUnitQuantity={singleUnitQuantity}
        />
        <OfferingDetailsAdditionalCosts price={price} type={type} />
      </ul>

      {!hideLocation && location && !isAtBusinessLocation && (
        <Typography variant="body02">
          <Location
            location={location as consumer.Location}
            business={business}
            isAtBusinessLocation={!!isAtBusinessLocation}
          />
        </Typography>
      )}
    </div>
  );
};

export default EventOfferingDetailsSimple;
